import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LanguageSwitcher from './LanguageSwitcher';
import './Navbar.css';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const Navbar = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navbarRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        // Navbar scroll effect
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        // Animate navbar on mount
        if (navbarRef.current) {
            gsap.fromTo(
                navbarRef.current,
                { y: -100, opacity: 0 },
                { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }
            );
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        // Update navbar style on scroll
        if (scrolled) {
            gsap.to(navbarRef.current, {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                duration: 0.3,
            });
        } else {
            gsap.to(navbarRef.current, {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                backdropFilter: 'blur(0px)',
                boxShadow: '0 0px 0px rgba(0, 0, 0, 0)',
                paddingTop: '1.5rem',
                paddingBottom: '1.5rem',
                duration: 0.3,
            });
        }
    }, [scrolled]);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
        if (!isOpen && menuRef.current) {
            gsap.fromTo(
                menuRef.current,
                { opacity: 0, x: -50 },
                { opacity: 1, x: 0, duration: 0.3, ease: 'power2.out' }
            );
        }
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setIsOpen(false);
        }
    };

    return (
        <nav ref={navbarRef} className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
            <div className="navbar-container">
                <div className="navbar-brand">
                    <div className="brand-logo">
                        <div className="logo-icon">⚕️</div>
                        <span className="brand-text">
                            <span className="brand-name">{t('navbar.brand.name')}</span>
                            <span className="brand-tagline">{t('navbar.brand.tagline')}</span>
                        </span>
                    </div>
                </div>

                <div className={`navbar-menu ${isOpen ? 'menu-open' : ''}`} ref={menuRef}>
                    <a href="#home" onClick={() => scrollToSection('home')} className="nav-link">
                        {t('navbar.links.home')}
                    </a>
                    <a href="#services" onClick={() => scrollToSection('services')} className="nav-link">
                        {t('navbar.links.services')}
                    </a>
                    <a href="#about" onClick={() => scrollToSection('about')} className="nav-link">
                        {t('navbar.links.about')}
                    </a>
                    <a href="#testimonials" onClick={() => scrollToSection('testimonials')} className="nav-link">
                        {t('navbar.links.testimonials')}
                    </a>
                    <a href="#contact" onClick={() => scrollToSection('contact')} className="nav-link">
                        {t('navbar.links.contact')}
                    </a>
                </div>

                <div className="navbar-actions">
                    <a href="tel:+918986637499" className="nav-phone">
                        <Phone size={18} />
                        <span>{t('navbar.phone')}</span>
                    </a>
                    <LanguageSwitcher />
                    <button className="btn-appointment" onClick={() => scrollToSection('contact')}>
                        {t('navbar.bookAppointment')}
                    </button>
                    <button className="menu-toggle" onClick={toggleMenu}>
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;

